/**
 * Принятие обновлённых документов (§4, §49).
 *
 * Экран появился после того, как публикация новой версии заперла всех, кто
 * зарегистрировался раньше: согласие привязано к версии, старая снимается
 * с публикации, и проверка начинает отказывать — «нужно принять условия»,
 * а принять негде. Сервер список присылал, читать его было некому.
 *
 * Показывается вместо приложения, а не баннером: без действующего согласия
 * заблокировано всё, ради чего человек пришёл, и предлагать ему ходить
 * по разделам было бы издевательством.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../api/auth';
import { useAuthStore } from '../../stores/auth';
import { Button } from '../ui/Button';
import { Card, CardBody } from '../ui/Card';
import { PageHeader } from '../ui/PageHeader';

export function PendingConsents({ pending }: { pending: string[] }) {
  const { t, i18n } = useTranslation();
  const setSession = useAuthStore((state) => state.setSession);
  const locale = i18n.resolvedLanguage ?? 'en';

  const [accepted, setAccepted] = useState<Record<string, boolean>>({});

  const documents = useQuery({
    queryKey: ['consent-documents', locale],
    queryFn: () => authApi.consentDocuments(locale),
    staleTime: 5 * 60_000,
  });

  const required = useMemo(
    () => (documents.data ?? []).filter((document) => pending.includes(document.type)),
    [documents.data, pending],
  );

  const allChecked = required.length > 0 && required.every((item) => accepted[item.type]);

  const accept = useMutation({
    mutationFn: () =>
      authApi.acceptConsents(
        Object.fromEntries(required.map((document) => [document.type, document.version])),
      ),
    onSuccess: async () => {
      // Перечитываем профиль: список непринятого считает сервер, и доверять
      // здесь своей догадке «теперь-то пусто» нельзя.
      setSession(await authApi.me());
    },
  });

  return (
    <div className="mx-auto flex min-h-full max-w-[640px] flex-col gap-6 px-5 py-12">
      <PageHeader title={t('consentUpdate.title')} description={t('consentUpdate.description')} />

      <Card>
        <CardBody className="flex flex-col gap-4">
          {required.map((document) => (
            <label key={document.type} className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1"
                checked={Boolean(accepted[document.type])}
                onChange={(event) =>
                  setAccepted((previous) => ({
                    ...previous,
                    [document.type]: event.target.checked,
                  }))
                }
              />
              <span className="text-body text-content-secondary">
                {t('consentUpdate.accept')}{' '}
                <Link
                  to={`/legal/${document.type}`}
                  className="text-accent hover:text-accent-hover"
                  target="_blank"
                >
                  {document.title.toLowerCase()}
                </Link>{' '}
                <span className="text-content-muted">
                  {t('legal.version', { value: document.version })}
                </span>
              </span>
            </label>
          ))}

          {accept.isError ? (
            <p className="text-meta text-error">{t('common.error')}</p>
          ) : null}

          <Button
            onClick={() => accept.mutate()}
            disabled={!allChecked || accept.isPending}
            className="self-start"
          >
            {accept.isPending ? t('common.saving') : t('consentUpdate.submit')}
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}
